import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, InputAdornment, TextField, Typography, useTheme } from '@mui/material';

import CommonButton from '../CommonButton';
import Loading from '@/component/mui/Backdrop';
import { useWeb3 } from '@/hooks/use-web3';
import {
  COIN_NAME_SFL,
  POLYGON_AMOY,
  POLYGON_MAIN,
  POLYGON_SHUFFLE,
  RES_OVER_BLOCK,
  RES_SUCCESS,
  RES_SUCCESS_201,
  STAKING_USER_STATE,
  STAKKINGTOKEN,
} from '@/lib/common/name-space';
import { toast } from 'react-toastify';
import SymbolSvg from '@/assets/assetfi/Symbol';
import CloseSvg from '@/assets/assetfi/Close';
import { CreateStakingUser, isCompleteModalType, PoolListType } from '@/types';
import BigNumber from 'bignumber.js';
import { useNavigate } from 'react-router-dom';
import UsdtBalance from '@/component/balance/UsdtBalance';
import { cutCommas, removeZeroStart } from '@/lib/util';
import { setTicker } from '@/component/balance/ticker';
import { checkToken, tokenPrice } from '@/api/staking/read';
import { decryptPassword } from '@/lib/Cryptos';
import { CheckTokenType } from '@/types/staking/read';
import { stakingUsers } from '@/api/staking/create';
import { addLocalSetStorageArr } from '@/lib/storage';
import ResultPopup from '@/lib/ResultPopup';
import { STATE_STAKE } from '@/lib/common/name-space/status';
import { packBigNumbers } from '@/lib/BigNumbers';
import { useWeb3AuthConnect } from '@web3auth/modal/react';

interface StakingModalPropsType {
  handleClose: () => void;
  open: boolean;
  contractAddr: string;
  detail: PoolListType;
  totalStakeUsdt: string;
}

const StakingModal = ({ handleClose, open, contractAddr, detail, totalStakeUsdt }: StakingModalPropsType) => {
  // theme
  const theme = useTheme();
  const { web3authCall, address: addr } = useWeb3();
  const { isConnected } = useWeb3AuthConnect();

  // state
  const [stake, setStake] = useState('0');
  const [showStake, setShowStake] = useState('0'); // 스테이킹 수량 입력할 때 보여지는 값
  const [usdt, setUsdt] = useState('0');
  const [isCompleteModal, setIsCompleteModal] = useState<isCompleteModalType>({
    success: false,
    fail: false,
    notice: false,
    pending: false,
  });
  // const errorMsg = useRef('');
  const [errorMsg, setErrorMsg] = useState('');
  const [ableAmt, setAbleAmt] = useState('0');

  const [isLoading, setIsLoading] = useState(false);
  const [isUpperMinAmount, setIsUpperMinAmount] = useState(false);
  const address = useRef<string>('');
  const navigate = useNavigate();
  const [isRetry, setIsRetry] = useState(false); // 50블록 넘었을 때 다시 시도

  useEffect(() => {
    if (address.current) {
      return;
    }
    (async () => {
      if (isConnected && addr) {
        address.current = addr;
      }
    })();
    return () => {};
  }, [web3authCall, isConnected, addr]);
  useEffect(() => {
    (async () => {
      if (open) {
        console.log(`open`);
        setUsdt(await setTicker({ navigate: navigate }));
      }
    })();

    return () => {};
  }, [open]);
  useEffect(() => {
    if (!open) {
      return;
    }
    ableMaxStaking();

    return () => {};
  }, [open, stake]);

  // 50블록 안에 채택이 되지 않을 시 다시 시도할 때
  useEffect(() => {
    if (isRetry) {
      (async () => {
        await handleConfirm();
        setIsRetry(false);
      })();
    }

    return () => {};
  }, [isRetry]);

  // method
  const ableMaxStaking = () => {
    // total 내가 staking 금액을 가져온다. 목표금액과 비교한다.
    // totalAmt
    // const temp = BigNumber(totalAmt).plus(stake).multipliedBy(usdt).minus(detail.maxFundraisingPrice).toFixed(6, 1);
    // setAbleAmt(temp);
    // const test = temp.gte(detail.maxFundraisingPrice);
    if (BigNumber(stake).isZero()) {
      return;
    }
    const temp = BigNumber(detail.goalMaxUsdt).dividedBy(usdt).minus(totalStakeUsdt).toFixed(6, 1);
    setAbleAmt(temp);
  };
  const handleConfirm = async () => {
    if (!stake) {
      return;
    }
    if (!address.current) {
      return;
    }
    const usdtStake = BigNumber(stake).multipliedBy(usdt);
    // 최소 스테이킹 금액 > 사용자 스테이킹 금액
    if (BigNumber(detail.minUsdt).gt(usdtStake)) {
      toast.error('Please check the minimum staking amount.');
      return;
    }
    // (스테이킹 된 수량 + 사용자 스테이킹 수량) * 코인가격 > 최대 모금 금액
    if (BigNumber(totalStakeUsdt).plus(usdtStake).gt(detail.goalMaxUsdt)) {
      toast.error('The maximum fundraising amount of the pool has been exceeded, so staking is not possible.');
      return;
    }
    handleClose();
    setIsLoading(true);

    let hash: string | null = null;

    try {
      // 1. 서버에서 스테이킹 토큰 가격 요청(암호화)
      const price = await tokenPrice();
      const decrypt = decryptPassword(price.data.value);

      const checkTokenType: CheckTokenType = {
        key: price.data.key,
        value: price.data.value,
        poolId: detail.poolId,
        stakeAmt: stake,
        address: address.current,
      };
      const check = await checkToken(checkTokenType);
      console.log(`decrypt: ${decrypt}`);
      console.log(check);

      // 연속해서 스테이킹 시도할 때 안되게 체크
      if (check.statusCode === RES_SUCCESS_201) {
        triggerIsCompleteModal('notice');
        setErrorMsg(check.data.msg);
        return;
      }
      if (String(decrypt) !== String(check.data)) {
        toast.error('error');
        return;
      }
      setUsdt(check.data);

      // 2. 실제 컨트랙트 실행
      const action = await actionStake();
      if (!action) {
        triggerIsCompleteModal('fail');
        setErrorMsg('Chain network error. Please try again in a moment.');
        return;
      }
      if (action.code === RES_OVER_BLOCK) {
        triggerIsCompleteModal('pending');
        setErrorMsg(action.msg);
        return;
      } else if (action.code !== RES_SUCCESS) {
        if (!action.hash) {
          triggerIsCompleteModal('fail');
          setErrorMsg(action.msg);
          return;
        }
        const createStakingUser: CreateStakingUser = {
          poolId: detail.poolId,
          address: address.current,
          stakeAmt: stake,
          hash: action.hash,
          stakeState: STAKING_USER_STATE.PENDING, // 5 펜딩 상태
        };
        await stakingUsers(createStakingUser, false);
        triggerIsCompleteModal('notice');
        setErrorMsg(action.msg);
        return;
      }
      hash = action.data.transactionHash;

      // 3. 서버에 저장
      const createStakingUser: CreateStakingUser = {
        poolId: detail.poolId,
        address: address.current,
        stakeAmt: stake,
        hash: hash!,
        stakeState: STAKING_USER_STATE.WAIT_STAKE, // 0 스테이킹 => 4 대기 상태
      };
      try {
        await stakingUsers(createStakingUser, false);
      } catch (e) {
        addLocalSetStorageArr('stake', createStakingUser);
      }

      triggerIsCompleteModal('success');
      setTimeout(() => {
        navigate('/pools');
      }, 5000);
    } catch (e: any) {
      console.error(e);
      triggerIsCompleteModal('fail');
      setErrorMsg(e?.message || JSON.stringify(e));
    } finally {
      setIsLoading(false);
    }
  };
  const triggerIsCompleteModal = (key: keyof isCompleteModalType) => {
    setIsCompleteModal((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  const actionStake = async () => {
    const payload = [address.current, stake];
    return await web3authCall!.callStaking(STAKKINGTOKEN, contractAddr, payload);
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: '320px',
            padding: '24px 0px',
            borderRadius: '20px',
          },
        }}
        open={open}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            padding: '0px 24px',
          }}
        >
          <SymbolSvg width={24} height={24} />
          <Typography color={theme.greyscale.darkgrey2} variant="h6">
            {detail.title}
          </Typography>

          <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
            <CloseSvg width={24} height={24} />
          </Box>
        </Box>
        <Box sx={{ marginTop: '24px', padding: '0px 16px', position: 'relative' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Stake Amount
          </Typography>

          <Typography
            sx={{ position: 'absolute', right: 24, bottom: 28 }}
            color={theme.greyscale.grey3}
            variant="body2"
          >
            {BigNumber(stake).multipliedBy(usdt).toFixed()} USD
          </Typography>
          <TextField
            value={showStake}
            placeholder="0"
            autoFocus
            onChange={(e) => {
              const deleteCommaValue = cutCommas(e.target.value);
              if (!e.target.value) {
                setStake('0');
                setShowStake('0');
                setIsUpperMinAmount(false);
                return;
              }
              setStake(removeZeroStart(deleteCommaValue));
              setShowStake(removeZeroStart(packBigNumbers(deleteCommaValue)));
              const amt = BigNumber(deleteCommaValue).multipliedBy(usdt).toFixed();
              if (BigNumber(amt).gt(detail.minUsdt)) {
                setIsUpperMinAmount(true);
              } else {
                setIsUpperMinAmount(false);
              }
            }}
            sx={{
              width: '100%',
              marginTop: '8px',

              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '& input': {
                  textAlign: 'right',
                  typography: 'h4',
                  color: theme.greyscale.darkgrey2,
                  padding: '8px',
                  paddingRight: '0px',
                  paddingBottom: '28px',
                },
                '& fieldset': {
                  borderColor: theme.background.blue,
                },
                '&:hover fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.MuiInputBase-root': {
                  background: '#357AFF1A',
                  padding: 0,
                },
                '& .MuiTypography-root': {
                  typography: 'h5',
                  color: theme.greyscale.darkgrey2,
                },
              },
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  sx={{
                    fontSize: '24px',
                    paddingRight: '8px',
                    paddingBottom: '20px',
                  }}
                  position="end"
                >
                  SFL
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ marginRight: '8px' }} align="right" variant="subtitle2" color={theme.greyscale.grey3}>
            Balance ~{' '}
            <UsdtBalance
              symbol={COIN_NAME_SFL}
              chainId={process.env.NODE_ENV === 'production' ? POLYGON_MAIN : POLYGON_AMOY}
              token={POLYGON_SHUFFLE}
            />
          </Typography>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            ● Amount Input Conditions
          </Typography>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey2}>
            ‘Stake Amount’ ≤ ‘Remaining Staking Limit’
          </Typography>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Remaining Staking Limit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {ableAmt}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Stake Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {showStake}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    SFL
                  </Typography>
                </Typography>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  ~ {BigNumber(stake).multipliedBy(usdt).toFixed()}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                APR
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.interestRate}%
                </Typography>
              </Box>
            </Box>
            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Duration
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  1 Year
                </Typography>
              </Box>
            </Box> */}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Minimum Deposit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.minUsdt}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Total Rewards
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  0 USD
                </Typography>
              </Box>
            </Box> */}
          </Box>
        </Box>
        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          {isUpperMinAmount ? (
            <CommonButton
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={stake ? '#ffffff' : theme.background.blue}
              bgColor={stake ? theme.background.blue : '#ffffff'}
              border={theme.background.blue}
            />
          ) : (
            <CommonButton
              disabled
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={theme.greyscale.lightgrey2}
              bgColor={theme.greyscale.lightgrey2}
              border={'#ffffff'}
            />
          )}
          <CommonButton
            sx={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: '10px',
              marginTop: '10px',
            }}
            text="Back"
            handleClick={handleClose}
            variant="h6"
            color={theme.greyscale.lightgrey2}
            border={theme.greyscale.lightgrey2}
            bgColor={'#ffffff'}
          />
        </Box>
      </Dialog>

      <ResultPopup
        isCompleteModal={isCompleteModal}
        triggerIsCompleteModal={triggerIsCompleteModal}
        errorMsg={errorMsg}
        type={STATE_STAKE.STAKE}
        setIsRetry={setIsRetry}
      />
      <Loading isLoading={isLoading} />
    </>
  );
};

export default StakingModal;
